import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Card from "react-bootstrap/Card";
import Form from "react-bootstrap/Form";

import {
  loadArticleListDataRequest,
  loadArticleDetailsRequest,
} from "../redux/Actions";
import Loader from "../../../../shared/components/Loader";

export default function ArticleList() {
  const dispatch = useDispatch();

  const { articles, loading: listLoading } = useSelector(
    (state) => state.articleList
  );
  const { article, loading: detailsLoading } = useSelector(
    (state) => state.articleDetails
  );

  const [search, setSearch] = useState("");

  // categoryId -> boolean (expanded/collapsed)
  const [expandedCategories, setExpandedCategories] = useState({});
  // selected article id for highlight
  const [selectedArticleId, setSelectedArticleId] = useState(null);
  // external link selection (if any)
  const [selectedExternal, setSelectedExternal] = useState(null); // { url, title }
  const [expandedArticles, setExpandedArticles] = useState({});
  const [selectedExternalLink, setSelectedExternalLink] = useState({
    articleId: null,
    linkIndex: null,
  });




  useEffect(() => {
    dispatch(loadArticleListDataRequest());
  }, [dispatch]);

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearch(value);
    dispatch(loadArticleListDataRequest(value));
  };

  const toggleCategory = (categoryId) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [categoryId]: !prev[categoryId],
    }));
  };

  const handleArticleClick = (item) => {
    setSelectedArticleId(item.id);

    // If article has ID → always fetch
    if (item.id) {
      dispatch(loadArticleDetailsRequest(item.id));
    }

    // Toggle article expansion for external links
    const hasExternalLink =
      Array.isArray(item.external_link) && item.external_link.length > 0;

    if (hasExternalLink) {
      setExpandedArticles((prev) => ({
        ...prev,
        [item.id]: !prev[item.id],
      }));
    }

    // Do NOT auto-open external link anymore
    setSelectedExternal(null);
  };

  const handleExternalLinkClick = (url, title) => {
    setSelectedExternal({ url, title });  // show in right container
  };

  // Styles
  const categoryHeaderStyle = {
    cursor: "pointer",
    padding: "8px 10px",
    borderRadius: "6px",
    marginBottom: "4px",
    fontWeight: 600,
    display: "flex",
    alignItems: "center",
    gap: "6px",
  };

  const articleItemStyle = (id) => ({
    cursor: "pointer",
    padding: "6px 10px",
    borderRadius: "6px",
    marginBottom: "4px",
    paddingLeft: "26px",
    transition: "0.2s",
    fontWeight: id === selectedArticleId ? "600" : "400",
    backgroundColor: id === selectedArticleId ? "#dce9ff" : "transparent",
    color: id === selectedArticleId ? "#003eaa" : "#333",
    display: "flex",
    alignItems: "center",
    gap: "6px",
  });

  const externalLinkStyle = (itemId, index) => {
    const isSelected =
      selectedExternalLink.articleId === itemId &&
      selectedExternalLink.linkIndex === index;

    return {
      cursor: "pointer",
      color: isSelected ? "#003eaa" : "#333",
      textDecoration: "underline",
      fontWeight: isSelected ? "600" : "400",
      backgroundColor: isSelected ? "#dce9ff" : "transparent",
      padding: "2px 4px",
      borderRadius: "4px",
      display: "inline-flex",
      alignItems: "center",
    };
  };


  const iconStyle = {
    display: "inline-block",
    width: "14px",
    textAlign: "center",
  };

  const categories = articles?.data || [];


  // Helpers (place these inside the component function)
  const renderExternalPreview = (external) => {
    if (!external || !external.url) return <p>Unsupported file format</p>;

    const url = external.url.toLowerCase();

    // PDF
    // if (url.endsWith(".pdf")) {
      return (
        <iframe
          title={external.title || "external-pdf"}
          src={external.url}
          style={{ width: "100%", height: "100%", border: "none" }}
        />
      );
    // }

    // Office files (doc, docx, xls, xlsx, pptx) — use Microsoft viewer
    // if (/\.(docx?|xlsx?|pptx?)$/i.test(url)) {
    //   const viewer = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
    //     external.url
    //   )}`;
    //   return (
    //     <iframe
    //       title={external.title || "external-office"}
    //       src={viewer}
    //       style={{ width: "100%", height: "100%", border: "none" }}
    //     />
    //   );
    // }

    // Images
    // if (/\.(png|jpe?g|gif|webp|svg)$/i.test(url)) {
    //   return (
    //     <img
    //       src={external.url}
    //       alt={external.title || "Article content"}
    //       style={{ maxWidth: "100%", maxHeight: "100%", display: "block" }}
    //     />
    //   );
    // }

    // Any other http(s) URL -> iframe preview
    // if (external.url.startsWith("http")) {
    //   return (
    //     <iframe
    //       title={external.title || "external-content"}
    //       src={external.url}
    //       style={{ width: "100%", height: "100%", border: "none" }}
    //     />
    //   );
    // }

    // return <p>Unsupported file format</p>;
  };

  const renderArticleContent = (article) => {
    const contentValue = article?.content || article?.data?.content || "";

    // If content is a file URL, show file viewer (reuse logic from external preview)
    if (typeof contentValue === "string" && contentValue.startsWith("http")) {
      if (/\.(pdf|docx?|xlsx?|pptx?)$/i.test(contentValue)) {
        const src = contentValue.endsWith(".pdf")
          ? contentValue
          : `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
            contentValue
          )}`;
        return (
          <iframe
            title={article.title || "article-file"}
            src={src}
            style={{ width: "100%", height: "70vh", border: "none" }}
          />
        );
      }

      // Generic URL
      return (
        <iframe
          title={article.title || "article-url"}
          src={contentValue}
          style={{ width: "100%", height: "70vh", border: "none" }}
        />
      );
    }

    // Default: render HTML content
    return (
      <div
        style={{ marginTop: 10, lineHeight: 1.7, fontSize: 15 }}
        dangerouslySetInnerHTML={{ __html: contentValue }}
      />
    );
  };

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "1fr 3fr",
        gap: "10px",
      }}
    >
      {/* LEFT SIDE: Tree View */}
      <div>
        <Form.Control
          type="text"
          placeholder="Search..."
          value={search}
          onChange={handleSearch}
          style={{ marginBottom: "10px", height: "40px" }}
        />

        <Card
          style={{
            padding: "15px",
            height: "73vh",
            overflowY: "auto",
            borderRadius: "10px",
          }}
        >
          <h5 className="fw-bold mb-3">Knowledge Base Articles</h5>

          {listLoading ? (
            <div>
              <Loader />
            </div>
          ) : (
            <div>
              {categories.map((category) => {
                const hasArticles =
                  Array.isArray(category.articles) &&
                  category.articles.length > 0;
                const isExpanded = !!expandedCategories[category.category_id];

                return (
                  <div
                    key={category.category_id}
                    style={{ marginBottom: "8px" }}
                  >
                    {/* Category Row */}
                    <div
                      style={categoryHeaderStyle}
                      onClick={() =>
                        hasArticles && toggleCategory(category.category_id)
                      }
                    >
                      {hasArticles && (
                        <i
                          className={
                            isExpanded
                              ? "fa fa-caret-down"
                              : "fa fa-caret-right"
                          }
                          style={{ fontSize: "12px", marginRight: "4px" }}
                        />
                      )}

                      {!hasArticles && <span style={iconStyle}></span>}
                      <span>{category.category_name}</span>
                    </div>

                    {/* Articles under category */}
                    {isExpanded && hasArticles && (
                      <ul style={{ listStyle: "none", paddingLeft: 0 }}>
                        {category.articles.map((item) => {
                          const hasExternalLink =
                            Array.isArray(item.external_link) &&
                            item.external_link.length > 0;

                          const isExpanded = expandedArticles[item.id] || false;

                          // shorten for display
                          const shorten = (url) =>
                            url.length > 45
                              ? url.substring(0, 45) + "..."
                              : url;

                          return (
                            <div key={item.id}>
                              {/* ARTICLE TITLE ROW — INDENTED UNDER CATEGORY */}
                              <li
                                style={{
                                  cursor: "pointer",
                                  padding: "4px 10px",
                                  paddingLeft: "30px",     //  consistent indent
                                  borderRadius: "6px",
                                  marginBottom: "4px",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "6px",
                                  fontWeight: item.id === selectedArticleId ? "600" : "400",
                                  backgroundColor: item.id === selectedArticleId ? "#dce9ff" : "transparent",
                                  color: item.id === selectedArticleId ? "#003eaa" : "#333",
                                }}
                                onClick={() => {
                                  handleArticleClick(item);
                                  setSelectedExternalLink({ articleId: null, linkIndex: null });
                                }}
                              >
                                {/* SMALL BULLET DOT */}
                                <i
                                  className="fa fa-circle"
                                  style={{ fontSize: "4px", marginRight: "6px" }}   //  smaller dot
                                />
                                {item.title}
                              </li>

                              {/* EXTERNAL URLS — SAME INDENT & SAME DOT */}
                              {Array.isArray(item.external_link) &&
                                item.external_link.length > 0 &&
                                item.external_link.map((link, index) => {
                                  const short =
                                    link.length > 45 ? link.substring(0, 45) + "..." : link;

                                  const isSelected =
                                    selectedExternalLink.articleId === item.id &&
                                    selectedExternalLink.linkIndex === index;

                                  return (
                                    <li
                                      key={index}
                                      style={{
                                        cursor: "pointer",
                                        padding: "4px 10px",
                                        paddingLeft: "30px",  //  EXACT SAME INDENT AS TITLE
                                        borderRadius: "6px",
                                        marginBottom: "4px",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "6px",
                                        fontWeight: isSelected ? "600" : "400",
                                        backgroundColor: isSelected ? "#dce9ff" : "transparent",
                                        color: isSelected ? "#003eaa" : "#333",
                                      }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedExternalLink({ articleId: item.id, linkIndex: index });
                                        setSelectedArticleId(null);
                                        handleExternalLinkClick(link, item.title);
                                      }}
                                    >
                                      {/* SAME SMALL DOT */}
                                      <i
                                        className="fa fa-circle"
                                        style={{ fontSize: "4px", marginRight: "6px" }} //  consistent small size
                                      />
                                      <span style={{ textDecoration: "underline" }}>{short}</span>
                                    </li>
                                  );
                                })}
                            </div>



                          );
                        })}
                      </ul>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </div>
      {/* RIGHT SIDE CONTENT */}
      <Card
        style={{
          padding: "25px",
          height: "80vh",
          overflowY: "auto",
          borderRadius: "10px",
        }}
      >
        {/* If an external link is selected, show that first */}
        {selectedExternal ? (
          <>
            {/* <h2 style={{ fontWeight: 700, marginBottom: 20 }}>
              {selectedExternal.title}
            </h2> */}

            {/* External Link Preview */}
            <div style={{ height: "100%", minHeight: "60vh" }}>
              {renderExternalPreview(selectedExternal)}
            </div>
          </>
        ) : article ? (
          <>
            {/* Title */}
            <h2 style={{ fontWeight: 700, marginBottom: 20 }}>
              {article.title}
            </h2>

            {/* MAIN ARTICLE CONTENT */}
            {renderArticleContent(article)}
          </>
        ) : detailsLoading ? (
          <div>
            <Loader />
          </div>
        ) : (
          <h4>Select an article</h4>
        )}
      </Card>{" "}
    </div>
  );
}
